import React, { useState, useEffect } from 'react';
import DashboardLayout from '../Layout/DashboardLayout';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../Common/LoadingSpinner';
import './Admin.css';

const AdminWallet = () => {
    const { getAllTransactions } = useAdmin();
    const [activeTab, setActiveTab] = useState('transactions');
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState({
        totalSystemBalance: 0,
        mtdCredits: 0,
        mtdDebits: 0,
        pendingTransactionsCount: 0
    });
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalTransactions: 0
    });

    useEffect(() => {
        fetchData();
    }, [pagination.currentPage, activeTab]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Example status filter based on tab: 'payouts' might just be a different type context if needed
            // For now, let's just fetch all transactions
            const response = await getAllTransactions({
                page: pagination.currentPage,
                limit: 20
            });

            if (response.success) {
                setTransactions(response.transactions);
                setStats(response.stats);
                setPagination(response.pagination);
            }
        } catch (error) {
            console.error('Error fetching admin transactions:', error);
            toast.error('Failed to load wallet data');
        } finally {
            setLoading(false);
        }
    };

    const getStatusClass = (status) => {
        if (!status) return 'status--info';
        switch (status.toLowerCase()) {
            case 'completed': return 'status--success';
            case 'pending': return 'status--warning';
            case 'failed': return 'status--error';
            case 'cancelled': return 'status--error';
            default: return 'status--info';
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    return (
        <DashboardLayout isAdmin={true}>
            <div className="admin-page">
                <header className="admin-header">
                    <h1>Wallet Management</h1>
                    {/* Add back manual processing logic if necessary, or link to User modal */}
                    {/* <button className="btn btn--primary">Manual Credit/Debit</button> */}
                </header>

                <div className="admin-stats-grid">
                    <div className="stat-card">
                        <span className="stat-label">Total System Balance</span>
                        <span className="stat-value">₹{(stats.totalSystemBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Total Credits (MTD)</span>
                        <span className="stat-value text-success">₹{(stats.mtdCredits || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Total Debits (MTD)</span>
                        <span className="stat-value text-danger">₹{(stats.mtdDebits || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Pending Settlements</span>
                        <span className="stat-value">{stats.pendingTransactionsCount || 0}</span>
                    </div>
                </div>

                <div className="admin-section">
                    <div className="admin-tabs mb-4 d-flex gap-4">
                        <button
                            className={`btn ${activeTab === 'transactions' ? 'btn--primary' : 'btn--outline'}`}
                            onClick={() => setActiveTab('transactions')}
                        >
                            All Transactions
                        </button>
                    </div>

                    <div className="admin-table-container">
                        {loading ? (
                            <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
                                <LoadingSpinner message="Loading live transactions..." />
                            </div>
                        ) : (
                            <>
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>TXN ID</th>
                                            <th>User</th>
                                            <th>Type</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.length > 0 ? transactions.map(txn => (
                                            <tr key={txn._id}>
                                                <td><code style={{ fontSize: '11px' }}>{txn.transactionId || txn._id.substring(0, 8)}</code></td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontWeight: 600 }}>{txn.userId?.name || 'Unknown User'}</span>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{txn.userId?.email}</span>
                                                    </div>
                                                </td>
                                                <td style={{ textTransform: 'capitalize' }}>{txn.category || txn.type}</td>
                                                <td><span style={{ fontWeight: 600 }} className={txn.type === 'credit' ? 'text-success' : 'text-danger'}>
                                                    {txn.type === 'credit' ? '+' : '-'}₹{(txn.amount || 0).toFixed(2)}
                                                </span></td>
                                                <td><span className={`status ${getStatusClass(txn.status)}`}>{txn.status}</span></td>
                                                <td>
                                                    {new Date(txn.createdAt).toLocaleString(undefined, { 
                                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                                    })}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', padding: '30px' }}>
                                                    No transactions found matching your criteria.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {/* Pagination */}
                                {pagination.totalPages > 1 && (
                                    <div className="pagination" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '10px' }}>
                                        <button 
                                            className="btn btn--outline btn--sm"
                                            disabled={pagination.currentPage === 1}
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                        >
                                            Previous
                                        </button>
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            Page {pagination.currentPage} of {pagination.totalPages}
                                        </span>
                                        <button 
                                            className="btn btn--outline btn--sm"
                                            disabled={pagination.currentPage === pagination.totalPages}
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdminWallet;

